<template>
  <div>
    <header-component />
    <div class="px-4 py-4">

      <div class="flex justify-between items-center mb-5">
        <h2 class="text-lg font-bold text-gray-800">Stock Movements</h2>
        <span class="text-sm text-gray-500">{{ meta.total || 0 }} records</span>
      </div>

      <!-- Filters -->
      <div class="flex flex-wrap gap-3 mb-4 items-end">
        <div>
          <label class="block text-xs text-gray-500 mb-1">Branch</label>
          <select v-model="filters.branch_id" @change="fetchMovements(1)"
            class="border border-gray-300 rounded px-3 py-2 text-sm">
            <option value="">All Branches</option>
            <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
          </select>
        </div>
        <div>
          <label class="block text-xs text-gray-500 mb-1">Product</label>
          <input v-model="filters.product_name" @input="onSearchInput" type="text"
            placeholder="Search product..." style="width:200px"
            class="border border-gray-300 rounded px-3 py-2 text-sm" />
        </div>
        <div>
          <label class="block text-xs text-gray-500 mb-1">Type</label>
          <select v-model="filters.movement_type" @change="fetchMovements(1)"
            class="border border-gray-300 rounded px-3 py-2 text-sm">
            <option value="">All Types</option>
            <option value="sale">Sale</option>
            <option value="transfer_in">Transfer In</option>
            <option value="transfer_out">Transfer Out</option>
            <option value="opening_stock">Opening Stock</option>
            <option value="adjustment">Adjustment</option>
            <option value="chalan_in">Chalan In</option>
            <option value="chalan_out">Chalan Out</option>
            <option value="sr_chalan_in">SR Chalan In</option>
            <option value="sr_chalan_out">SR Chalan Out</option>
            <option value="damage">Damage / Spoilage</option>
            <option value="expired">Expired / Rusted</option>
            <option value="loss">Loss / Theft</option>
            <option value="surplus">Surplus Found</option>
          </select>
        </div>
        <div>
          <label class="block text-xs text-gray-500 mb-1">From Date</label>
          <input v-model="filters.from_date" type="date" @change="fetchMovements(1)"
            class="border border-gray-300 rounded px-3 py-2 text-sm" />
        </div>
        <div>
          <label class="block text-xs text-gray-500 mb-1">To Date</label>
          <input v-model="filters.to_date" type="date" @change="fetchMovements(1)"
            class="border border-gray-300 rounded px-3 py-2 text-sm" />
        </div>
        <div>
          <button @click="clearFilters"
            class="border border-gray-300 rounded px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">
            Clear
          </button>
        </div>
      </div>

      <div v-if="loading" class="flex justify-center py-12">
        <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
      </div>

      <div v-else class="overflow-x-auto">
        <table class="min-w-full bg-white border border-gray-200 rounded-lg text-sm">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Branch</th>
              <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
              <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th class="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Quantity</th>
              <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
              <th class="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">By</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="rows.length === 0">
              <td colspan="7" class="text-center py-12 text-gray-400">No movements found.</td>
            </tr>
            <tr v-for="row in rows" :key="row.id" class="border-b hover:bg-gray-50">
              <td class="px-3 py-2 text-gray-600 whitespace-nowrap">{{ formatDate(row.created_at) }}</td>
              <td class="px-3 py-2 text-gray-800">{{ row.branch_name }}</td>
              <td class="px-3 py-2 text-gray-800">{{ row.product_name }}</td>
              <td class="px-3 py-2">
                <span :class="typeBadgeClass(row.movement_type)"
                  class="inline-block px-2 py-0.5 rounded text-xs font-medium">
                  {{ formatType(row.movement_type) }}
                </span>
              </td>
              <td class="px-3 py-2 text-right font-mono font-semibold"
                :class="row.quantity >= 0 ? 'text-green-600' : 'text-red-500'">
                {{ row.quantity >= 0 ? '+' : '' }}{{ row.quantity }}
              </td>
              <td class="px-3 py-2 text-gray-500 text-xs">
                <span v-if="row.reference_type">{{ row.reference_type }}</span>
                <span v-if="row.notes" class="ml-1 italic">{{ row.notes }}</span>
              </td>
              <td class="px-3 py-2 text-gray-500 text-xs">{{ row.created_by_name }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div v-if="meta.pages > 1" class="flex items-center justify-between mt-4">
        <span class="text-sm text-gray-500">
          Page {{ meta.page }} of {{ meta.pages }}
        </span>
        <div class="flex gap-2">
          <button @click="fetchMovements(meta.page - 1)" :disabled="meta.page <= 1"
            class="px-3 py-1 border rounded text-sm disabled:opacity-40 hover:bg-gray-50">
            Prev
          </button>
          <button @click="fetchMovements(meta.page + 1)" :disabled="meta.page >= meta.pages"
            class="px-3 py-1 border rounded text-sm disabled:opacity-40 hover:bg-gray-50">
            Next
          </button>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import Api from "@/middleware/api";
import endpoints from "@/utils/constants";

var BASE = endpoints.LARAVEL_BASE_URL;
var searchTimer = null;

var TYPE_LABELS = {
  sale:                 "Sale",
  transfer_in:          "Transfer In",
  transfer_out:         "Transfer Out",
  opening_stock:        "Opening Stock",
  adjustment:           "Manual Correction",
  chalan_in:            "Chalan In",
  chalan_out:           "Chalan Out",
  chalan_in_reversal:   "Chalan In (Rev)",
  chalan_out_reversal:  "Chalan Out (Rev)",
  sr_chalan_in:         "SR Chalan In",
  sr_chalan_out:        "SR Chalan Out",
  damage:               "Damage / Spoilage",
  expired:              "Expired / Rusted",
  loss:                 "Loss / Theft",
  surplus:              "Surplus Found",
};

var TYPE_CLASSES = {
  sale:                 "bg-red-100 text-red-700",
  transfer_in:          "bg-green-100 text-green-700",
  transfer_out:         "bg-orange-100 text-orange-700",
  opening_stock:        "bg-blue-100 text-blue-700",
  adjustment:           "bg-gray-100 text-gray-700",
  chalan_in:            "bg-teal-100 text-teal-700",
  chalan_out:           "bg-yellow-100 text-yellow-700",
  chalan_in_reversal:   "bg-gray-100 text-gray-500",
  chalan_out_reversal:  "bg-gray-100 text-gray-500",
  sr_chalan_in:         "bg-teal-100 text-teal-700",
  sr_chalan_out:        "bg-yellow-100 text-yellow-700",
  damage:               "bg-orange-100 text-orange-700",
  expired:              "bg-yellow-100 text-yellow-700",
  loss:                 "bg-red-100 text-red-700",
  surplus:              "bg-green-100 text-green-700",
};

export default {
  name: "StockMovements",
  data() {
    return {
      branches: [],
      filters: {
        branch_id:     "",
        product_name:  "",
        movement_type: "",
        from_date:     "",
        to_date:       "",
      },
      rows: [],
      meta: { total: 0, page: 1, per_page: 50, pages: 1 },
      loading: false,
    };
  },
  created() {
    this.fetchBranches();
    this.fetchMovements(1);
  },
  methods: {
    async fetchBranches() {
      try {
        var res = await new Api().call("get", BASE + "/api/v1/branches");
        this.branches = res.data.data || res.data;
      } catch (e) {
        console.error(e);
      }
    },

    onSearchInput() {
      clearTimeout(searchTimer);
      var self = this;
      searchTimer = setTimeout(function() { self.fetchMovements(1); }, 400);
    },

    async fetchMovements(page) {
      this.loading = true;
      try {
        var params = new URLSearchParams({ page: page || 1, per_page: 50 });
        if (this.filters.branch_id)     params.set("branch_id",     this.filters.branch_id);
        if (this.filters.product_name)  params.set("product_name",  this.filters.product_name);
        if (this.filters.movement_type) params.set("movement_type", this.filters.movement_type);
        if (this.filters.from_date)     params.set("from_date",     this.filters.from_date);
        if (this.filters.to_date)       params.set("to_date",       this.filters.to_date);

        var res = await new Api().call("get", BASE + "/api/v1/stock-movements?" + params.toString());
        this.rows = res.data.data;
        this.meta = res.data.meta;
      } catch (e) {
        console.error(e);
        window.notify({ message: "Failed to load stock movements", type: "danger", title: "Error" });
      } finally {
        this.loading = false;
      }
    },

    clearFilters() {
      this.filters = { branch_id: "", product_name: "", movement_type: "", from_date: "", to_date: "" };
      this.fetchMovements(1);
    },

    formatDate(dt) {
      if (!dt) return "";
      var d = new Date(dt);
      return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
        + " " + d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
    },

    formatType(type) {
      return TYPE_LABELS[type] || type;
    },

    typeBadgeClass(type) {
      return TYPE_CLASSES[type] || "bg-gray-100 text-gray-700";
    },
  },
};
</script>
